// Pre-generated artwork of one theme: a base image plus one transparent
// layer per item (see the app's twenty_challenge_visuals.dart). The web game
// never generates images; it only stacks the layers a player has won.
//
//  * `scene`: full-frame RGBA layers aligned to the base. With layout >= 2
//    every layer carries its baked `box`, and the won items are re-placed by
//    the planner (layout.ts) and drawn as outlined stickers.
//  * `stack`: cropped sprites stacked bottom-up (burger, cocktail, ...); a
//    `cap` always sits on top and the frame grows upward with the stack.

import { Offset, Rect } from './geom';
import { planLayout, type LayoutSpec, type PlacedItem } from './layout';

export interface LayerSpec {
  file: string;
  thumb: string | null;
  z: number;
  w: number;
  h: number;
  thickness: number;
  anchor: number;
  box: Rect | null;
  kind: string;
  size: number;
  hidden: boolean;
}

export interface ThemeVisual {
  id: string;
  dir: string; // URL prefix, ends without slash
  model: 'scene' | 'stack';
  width: number;
  height: number;
  backdrop: string;
  layers: Map<number, LayerSpec>;
  cap: LayerSpec | null;
  stack: { cx: number; topY: number } | null;
  layout: number;
  archetype: string;
  horizon: number | null;
  keepout: Rect | null;
  host: Rect | null;
  surface: Rect | null;
  surfaceIsEllipse: boolean;
  lineup: boolean;
  soft: boolean;
}

export const SIZE_FACTORS: Record<string, number> = { S: 0.55, M: 0.8, L: 1.0, XL: 1.35 };

type Json = Record<string, unknown>;

function ltrb(v: unknown): Rect | null {
  if (!Array.isArray(v) || v.length !== 4) return null;
  const [l, t, r, b] = v.map(Number);
  return Rect.fromLTRB(l, t, r, b);
}

function layerFromJson(j: Json): LayerSpec {
  const b = j.box as number[] | undefined;
  return {
    file: String(j.file),
    thumb: (j.thumb as string | undefined) ?? null,
    z: Number(j.z ?? 0),
    w: Number(j.w ?? 0),
    h: Number(j.h ?? 0),
    thickness: Number(j.thickness ?? 0),
    anchor: Number(j.anchor ?? 0.5),
    box: Array.isArray(b) && b.length === 4 ? Rect.fromLTWH(b[0], b[1], b[2], b[3]) : null,
    kind: (j.kind as string | undefined) ?? 'floor',
    size: SIZE_FACTORS[(j.size as string | undefined) ?? 'M'] ?? 0.8,
    hidden: (j.hidden as boolean | undefined) ?? false,
  };
}

export function visualFromJson(id: string, dir: string, j: Json): ThemeVisual {
  const layers = new Map<number, LayerSpec>();
  for (const [k, v] of Object.entries(j.layers as Record<string, Json>)) {
    layers.set(Number(k), layerFromJson(v));
  }
  const model = j.model === 'stack' ? 'stack' : 'scene';
  const stack = j.stack as { cx: number; topY: number } | undefined;
  return {
    id,
    dir,
    model,
    width: Number(j.width),
    height: Number(j.height),
    backdrop: (j.backdrop as string | undefined) ?? '#000000',
    layers,
    cap: j.cap ? layerFromJson(j.cap as Json) : null,
    stack: stack ? { cx: Number(stack.cx), topY: Number(stack.topY) } : null,
    layout: Number(j.layout ?? 1),
    archetype: (j.archetype as string | undefined) ?? (model === 'stack' ? 'stack' : 'room'),
    horizon: j.horizon == null ? null : Number(j.horizon),
    keepout: ltrb(j.keepout),
    host: ltrb(j.host),
    surface: ltrb(j.surface),
    surfaceIsEllipse: j.surfaceShape === 'ellipse',
    lineup: j.lineup === true,
    soft: (j.soft as boolean | undefined) ?? false,
  };
}

export const basePath = (v: ThemeVisual) =>
  v.soft && v.model !== 'stack' ? `${v.dir}/base_soft.webp` : `${v.dir}/base.webp`;

export const layerPath = (v: ThemeVisual, itemIndex: number) => `${v.dir}/${v.layers.get(itemIndex)!.file}`;

export const itemImagePath = (v: ThemeVisual, itemIndex: number) => {
  const t = v.layers.get(itemIndex)?.thumb;
  return t ? `${v.dir}/${t}` : null;
};

export const hasPlannedLayout = (v: ThemeVisual) =>
  v.model === 'scene' && v.layout >= 2 && [...v.layers.values()].every((l) => l.box !== null || l.hidden);

export function layoutSpec(v: ThemeVisual): LayoutSpec {
  return {
    archetype: v.archetype,
    canvas: { width: v.width, height: v.height },
    horizon: v.horizon,
    keepout: v.keepout,
    host: v.host,
    surface: v.surface,
    surfaceIsEllipse: v.surfaceIsEllipse,
    lineup: v.lineup,
  };
}

// ─── Planning ──────────────────────────────────────────────────────────────

const plans = new Map<string, PlacedItem[]>();

/// The planned stickers for [won] (theme item indices), in paint order. The
/// planner can take up to ~100 ms for a full build, so recent plans are kept.
export function planScene(v: ThemeVisual, won: number[]): PlacedItem[] {
  const key = `${v.dir}|${won.join(',')}`;
  const hit = plans.get(key);
  if (hit) {
    plans.delete(key);
    plans.set(key, hit);
    return hit;
  }
  const items = won
    .filter((i) => v.layers.get(i)?.box)
    .map((i) => {
      const l = v.layers.get(i)!;
      return { itemIndex: i, box: l.box!, kind: l.kind, sizeFactor: l.size };
    });
  const placed = planLayout(layoutSpec(v), items).sort((a, b) => a.drawOrder - b.drawOrder);
  if (plans.size >= 48) plans.delete(plans.keys().next().value!);
  plans.set(key, placed);
  return placed;
}

export interface StackPlaced {
  itemIndex: number; // -1 = the cap
  spec: LayerSpec;
  y: number; // top of the sprite, base px
}

export interface StackFrame {
  placed: StackPlaced[];
  left: number;
  top: number;
  side: number;
}

/// Plans the stack in base pixels (bottom-up) and frames it: a square from
/// the base's bottom edge up to just above the top sprite, at least the
/// widest sprite x 1.6 wide, centred on the stack and kept inside the base
/// when it fits. A rise above 0.8 H is compressed; the cap sits on top.
export function stackFrame(v: ThemeVisual, won: number[]): StackFrame {
  const g = v.stack!;
  const order = won.filter((i) => v.layers.has(i)).sort((a, b) => v.layers.get(a)!.z - v.layers.get(b)!.z);
  const total = order.reduce((a, i) => a + v.layers.get(i)!.h * v.layers.get(i)!.thickness, 0);
  const k = total > 0 ? Math.min(1.0, (0.8 * v.height) / total) : 1.0;
  const placed: StackPlaced[] = [];
  let rise = 0;
  for (const i of order) {
    const s = v.layers.get(i)!;
    placed.push({ itemIndex: i, spec: s, y: g.topY - rise - s.h * s.anchor });
    rise += k * s.h * s.thickness;
  }
  if (v.cap) placed.push({ itemIndex: -1, spec: v.cap, y: g.topY - rise - v.cap.h * v.cap.anchor });
  const maxW = Math.max(0, ...[...v.layers.values()].map((s) => s.w), v.cap?.w ?? 0);
  let top = g.topY - 20;
  for (const p of placed) if (p.y - 20 < top) top = p.y - 20;
  const side = Math.max(maxW > 0 ? maxW * 1.6 : v.width, v.height - top);
  let left = g.cx - side / 2;
  if (side <= v.width) left = Math.min(Math.max(left, 0), v.width - side);
  return { placed, left, top: v.height - side, side };
}

// ─── Loading ───────────────────────────────────────────────────────────────

const cache = new Map<string, Promise<ThemeVisual | null>>();

/// The artwork of [themeId], null without any (text themes, blocklisted).
export function loadVisual(contentRoot: string, themeId: string): Promise<ThemeVisual | null> {
  let p = cache.get(themeId);
  if (!p) {
    const dir = `${contentRoot}/${themeId}`;
    p = fetch(`${dir}/visual.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => (j ? visualFromJson(themeId, dir, j as Json) : null))
      .catch(() => null);
    cache.set(themeId, p);
  }
  return p;
}

const images = new Map<string, Promise<HTMLImageElement>>();

export function loadImage(src: string): Promise<HTMLImageElement> {
  let p = images.get(src);
  if (!p) {
    p = new Promise((resolve, reject) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`image ${src}`));
      img.src = src;
    });
    images.set(src, p);
    p.catch(() => images.delete(src));
  }
  return p;
}

/// Fetches every picture a round can show, so a won item pops in without
/// a flicker.
export function precacheVisual(v: ThemeVisual, items: number[]): void {
  void loadImage(basePath(v)).catch(() => {});
  for (const i of items) {
    if (v.layers.has(i)) void loadImage(layerPath(v, i)).catch(() => {});
    const t = itemImagePath(v, i);
    if (t) void loadImage(t).catch(() => {});
  }
  if (v.cap) void loadImage(`${v.dir}/${v.cap.file}`).catch(() => {});
}

export { Offset };
